import { useUser } from "@saleor/auth";
import { isStaffRole } from "@saleor/auth/hooks/useUserRole";
import AppHeader from "@saleor/components/AppHeader";
import Container from "@saleor/components/Container";
import MyTypography from "@saleor/components/MyTypography";
import PageHeader from "@saleor/components/PageHeader";
import ShoppingCartDetailCustomer from "@saleor/createOrder/components/ShoppingCartDetailCustomer";
import {
  orderCartPath,
  OrderDetailQueryParams,
  orderDetailUrl
} from "@saleor/createOrder/urls";
import {
  ContractOrderLineInput,
  CustomerOrderLineInput,
  ScgpCustomerErrorCode,
  useCreateCustomerOrderMutation,
  useCreateOrderMutation
} from "@saleor/graphql";
import useNavigator from "@saleor/hooks/useNavigator";
import { makeStyles } from "@saleor/macaw-ui";
import React, { useState } from "react";
import { useIntl } from "react-intl";
import useRouter from "use-react-router";

import DialogCreateOrderError from "../components/DialogCreateOrderError";
import ShoppingCartDetail from "../components/ShoppingCartDetail";
const useStyles = makeStyles(
  theme => ({
    header: {
      marginBottom: 36
    },
    inlineBlock: {
      display: "inline-block",
      color: theme.palette.grey[900]
    }
  }),
  { name: "CartView" }
);

interface CartViewProps {
  id: string;
  params: OrderDetailQueryParams;
}

const CartDetail: React.FC<CartViewProps> = ({ id, params }) => {
  const intl = useIntl();
  const { history } = useRouter();
  const classes = useStyles();
  const navigate = useNavigator();
  const { user } = useUser();
  const [openCreateOrderError, setOpenCreateOrderError] = useState<boolean>(
    false
  );
  const [openModalDiffContract, setOpenModalDiffContract] = useState<boolean>(
    false
  );
  const toggleCreateOrderError = () => {
    setOpenCreateOrderError(val => !val);
  };

  const toggleModalDiffContract = () => {
    setOpenModalDiffContract(val => !val);
  };
  const [popUpContent, setPopUpContent] = useState(null);
  const [handleCreateOrder] = useCreateOrderMutation({
    onCompleted: res => {
      // if (
      //   res.createContractOrder.order === null &&
      //   res.createContractOrder.errors.length === 0
      // ) {
      //   toggleCreateOrderError();
      //   return;
      // }
      if (
        res.createContractOrder.order === null &&
        res.createContractOrder.errors.length !== 0
      ) {
        if (res.createContractOrder.errors[0].code === "CONTRACT_ERROR") {
          toggleModalDiffContract();
          setPopUpContent(
            <MyTypography variant="body2" className={classes.inlineBlock}>
              กรุณาเลือก{" "}
              <MyTypography variant="subtitle2" className={classes.inlineBlock}>
                เลขที่ Contract
              </MyTypography>{" "}
              หมายเลขเดียวกัน ในการสร้างคำสั่งซื้อ
            </MyTypography>
          );
          return;
        } else if (
          res.createContractOrder.errors[0].code === "PRODUCT_GROUP_ERROR"
        ) {
          toggleModalDiffContract();

          setPopUpContent(
            <MyTypography variant="body2" className={classes.inlineBlock}>
              กรุณาเลือก{" "}
              <MyTypography variant="subtitle2" className={classes.inlineBlock}>
                สินค้ากลุ่มเดียวกัน
              </MyTypography>{" "}
              ในการสร้างคำสั่งซื้อ
            </MyTypography>
          );
        }
      }
      navigate(orderCartPath(res.createContractOrder?.order?.id));
    }
  });
  const [handleCreateCustomerOrder] = useCreateCustomerOrderMutation({
    onCompleted: res => {
      if (
        res?.createCustomerOrder?.errors[0]?.code ===
        ScgpCustomerErrorCode.PRODUCT_GROUP_ERROR
      ) {
        toggleModalDiffContract();
        setPopUpContent(
          <MyTypography variant="body2" className={classes.inlineBlock}>
            กรุณาเลือก{" "}
            <MyTypography variant="subtitle2" className={classes.inlineBlock}>
              สินค้ากลุ่มเดียวกัน
            </MyTypography>{" "}
            ในการสร้างคำสั่งซื้อ
          </MyTypography>
        );
        return;
      } else if (
        res.createCustomerOrder.order === null &&
        res.createCustomerOrder.errors.length === 0
      ) {
        toggleCreateOrderError();
        return;
      }
      navigate(orderCartPath(res.createCustomerOrder.order.id));
    }
  });

  const submitCartDetail = (data, customerId, contractId?) => {
    if (user.isStaff) {
      const getLines: ContractOrderLineInput[] = data?.lines.map(line => ({
        checkoutLineId: line?.id,
        quantity: line?.quantity,
        requestDate: line?.requestDate || undefined,
        plant: line?.plant || "",
        shipTo: line?.shipTo || "",
        internalCommentsToWarehouse: line?.internalCommentsToWarehouse || "",
        productInformation: line?.productInformation || "",
        variantId: line.variantId
      }));
      handleCreateOrder({
        variables: {
          input: {
            orderInformation: { customerId, contractId },
            lines: getLines
          }
        }
      });
    } else {
      const getLines: CustomerOrderLineInput[] = data?.lines.map(line => ({
        cartItemId: line?.id,
        contractProductId: line?.contractProductId,
        variantId: line?.variantId,
        quantity: line?.quantity
      }));
      handleCreateCustomerOrder({
        variables: {
          input: {
            orderInformation: { contractId: customerId },
            lines: getLines
          }
        }
      });
    }
  };

  const handleBack = () => {
    history.goBack();
  };

  return (
    <>
      <Container>
        <AppHeader onBack={handleBack}>
          <MyTypography variant="body3">
            {intl.formatMessage({ defaultMessage: "กลับไป" })}
          </MyTypography>
        </AppHeader>
        <PageHeader
          title={intl.formatMessage({ defaultMessage: "Shopping Cart" })}
        />
        <MyTypography variant="h7" className={classes.header}>
          สินค้าในตะกร้า
        </MyTypography>
        {user.isStaff ? (
          <ShoppingCartDetail
            checkOutId={id}
            onBack={() => navigate(orderDetailUrl(""))}
            onSubmitCart={submitCartDetail}
            params={params}
          />
        ) : (
          <ShoppingCartDetailCustomer
            cartId={id}
            onSubmitCart={submitCartDetail}
          />
        )}
        <DialogCreateOrderError
          onConfirm={toggleCreateOrderError}
          open={openCreateOrderError}
          okText="ปิดหน้าต่าง"
          title={
            isStaffRole()
              ? "ไม่สามารถดำเนินการต่อได้"
              : "ลูกค้ารายนี้ติด Credit Block"
          }
          content={
            <span>
              <MyTypography variant="body2" className={classes.inlineBlock}>
                {isStaffRole() ? (
                  <>
                    เนื่องจาก Customer Credit :{" "}
                    <MyTypography
                      variant="subtitle2"
                      className={classes.inlineBlock}
                    >
                      Credit Blocked
                    </MyTypography>
                  </>
                ) : (
                  "เนื่องจากลูกค้ารายนี้ติด Credit Block กรุณาติดต่อฝ่ายสินเชื่อ"
                )}
              </MyTypography>
            </span>
          }
        />
        <DialogCreateOrderError
          onConfirm={toggleModalDiffContract}
          open={openModalDiffContract}
          okText="ปิดหน้าต่าง"
          title="ไม่สามารถดำเนินการได้"
          content={popUpContent}
        />
      </Container>
    </>
  );
};

export default CartDetail;
